import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Upload, 
  FileText, 
  AlertCircle, 
  CheckCircle2, 
  UserPlus, 
  UserCheck,
  X,
  Eye
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const CustomerCsvImport = ({ onImportComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'importing', 'complete'
  const { toast } = useToast();
  const { user } = useAuth();

  // Only admins can import
  if (user?.role !== 'admin') {
    return null;
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        toast({
          title: "Invalid file type",
          description: "Please select a CSV file",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
      setPreviewData(null);
      setImportResult(null);
      setStep('upload');
    }
  };

  const previewImport = async () => {
    if (!selectedFile) return;

    try {
      setIsProcessing(true);
      const response = await api.previewCustomerImport(selectedFile);
      setPreviewData(response.data);
      setStep('preview');
      
      toast({
        title: "Preview generated",
        description: `Found ${response.data.total_rows} rows to process`,
      });
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: "Preview failed",
        description: error.response?.data?.detail || "Unable to preview CSV file",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const performImport = async () => {
    if (!selectedFile) return;

    try {
      setIsProcessing(true);
      setStep('importing');
      
      const response = await api.importCustomers(selectedFile);
      setImportResult(response.data);
      setStep('complete');
      
      toast({
        title: "Import completed",
        description: `Created ${response.data.created} customers, updated ${response.data.updated} customers`,
      });

      // Notify parent component to refresh customer list
      if (onImportComplete) {
        onImportComplete();
      }
      
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import failed",
        description: error.response?.data?.detail || "Unable to import CSV file",
        variant: "destructive",
      });
      setStep('preview');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setImportResult(null);
    setStep('upload');
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Import Customers from CSV (Xero Format)
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {step === 'upload' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      Upload Xero Contacts CSV
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">
                      Supports ContactName, EmailAddress, Phone, Address fields
                    </span>
                  </label>
                  <Input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>

            {selectedFile && (
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">{selectedFile.name}</p>
                    <p className="text-xs text-blue-600">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={previewImport} disabled={isProcessing}>
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setSelectedFile(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'preview' && previewData && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Import Preview</h3>
              <Button variant="outline" onClick={resetImport}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{previewData.total_rows}</div>
                  <div className="text-sm text-gray-600">Total Rows</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{previewData.created}</div>
                  <div className="text-sm text-gray-600">New Customers</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{previewData.updated}</div>
                  <div className="text-sm text-gray-600">Updates</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{previewData.errors?.length || 0}</div>
                  <div className="text-sm text-gray-600">Errors</div>
                </CardContent>
              </Card>
            </div>

            {/* Preview Data Table */}
            {previewData.preview_data?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Preview Data (First 50 rows)</h4>
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Row</th>
                          <th className="px-3 py-2 text-left">Action</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Company</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.preview_data.map((item, index) => (
                          <tr key={item.row ?? index} className="border-t">
                            <td className="px-3 py-2">{item.row}</td>
                            <td className="px-3 py-2">
                              <Badge variant={item.action === 'create' ? 'default' : 'secondary'}>
                                {item.action === 'create' ? (
                                  <>
                                    <UserPlus className="h-3 w-3 mr-1" />
                                    Create
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="h-3 w-3 mr-1" />
                                    Update
                                  </>
                                )}
                              </Badge>
                            </td>
                            <td className="px-3 py-2">{item.data.name}</td>
                            <td className="px-3 py-2">{item.data.email || '-'}</td>
                            <td className="px-3 py-2">{item.data.company || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Errors */}
            {previewData.errors?.length > 0 && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription>
                  <div className="font-medium text-red-800 mb-2">
                    {previewData.errors.length} rows have errors and will be skipped:
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {previewData.errors.slice(0, 5).map((error, index) => (
                      <div key={`${error.row}-${index}`} className="text-sm text-red-700">
                        Row {error.row}: {error.errors?.join(', ') || error.error}
                      </div>
                    ))}
                    {previewData.errors.length > 5 && (
                      <div className="text-sm text-red-600">
                        ... and {previewData.errors.length - 5} more errors
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Import Button */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep('upload')}>
                Back to Upload
              </Button>
              <Button onClick={performImport} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import {previewData.created + previewData.updated} Customers
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium">Importing Customers...</h3>
            <p className="text-gray-600">Please wait while we process your CSV file</p>
          </div>
        )}

        {step === 'complete' && importResult && (
          <div className="space-y-4">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-green-900">Import Completed Successfully</h3>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{importResult.created}</div>
                  <div className="text-sm text-gray-600">Created</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{importResult.updated}</div>
                  <div className="text-sm text-gray-600">Updated</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{importResult.errors?.length || 0}</div>
                  <div className="text-sm text-gray-600">Errors</div>
                </CardContent>
              </Card>
            </div>

            {importResult.errors?.length > 0 && (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription>
                  <div className="font-medium text-yellow-800 mb-2">
                    Some rows were skipped due to errors:
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto text-sm text-yellow-700">
                    {importResult.errors.slice(0, 5).map((error, index) => (
                      <div key={`${error.row}-${index}`}>Row {error.row}: {error.error}</div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="text-center">
              <Button onClick={resetImport}>
                Import Another File
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerCsvImport;